import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { httpAdmin } from '../lib/http';
import { useI18n } from '../contexts/I18n';
import ELogo from './ELogo';
import './AuthForms.css';
import './AdminDashboard.css';

const PUB = process.env.PUBLIC_URL || '';
const ADMIN_WELCOME_VIDEO = `${PUB}/videos/welcome.mp4`;
const ADMIN_HERO_LOGO = `${PUB}/images/ehgezly-hero-logo.png?v=4`;

async function readError(res) {
  let data = {};
  try {
    data = await res.json();
  } catch {
    /* ignore */
  }
  const err = new Error(data?.message || res.statusText || 'Request failed');
  err.status = res.status;
  err.code = data?.code;
  err.retryAfter = data?.retryAfter;
  err.attemptsRemaining = data?.attemptsRemaining;
  throw err;
}

function AdminBrandAside({ label }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      el.pause();
      return undefined;
    }
    el.muted = true;
    const play = () => {
      const p = el.play();
      if (p?.catch) p.catch(() => {});
    };
    play();
    el.addEventListener('loadeddata', play);
    return () => el.removeEventListener('loadeddata', play);
  }, []);

  return (
    <aside className="auth-brand auth-brand--showcase" aria-label={label}>
      <div className="auth-brand__video" aria-hidden="true">
        <video
          ref={videoRef}
          className="auth-brand__video-el"
          src={ADMIN_WELCOME_VIDEO}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        />
        <div className="auth-brand__video-veil" />
      </div>
      <div className="auth-brand__hero-mark">
        <div className="auth-brand__hero-logo-wrap">
          <img
            src={ADMIN_HERO_LOGO}
            alt="Ehgezly"
            className="auth-brand__hero-logo"
            draggable="false"
          />
        </div>
      </div>
    </aside>
  );
}

function SessionBanner() {
  const [params] = useSearchParams();
  const reason = params.get('reason');
  if (reason === 'session') {
    return (
      <div className="admin-login-banner admin-login-banner--info" role="status">
        Session expired — sign in again to continue.
      </div>
    );
  }
  if (reason === 'logout') {
    return (
      <div className="admin-login-banner admin-login-banner--info" role="status">
        You have been signed out.
      </div>
    );
  }
  return null;
}

/** Login + MFA step */
export function AdminLoginPage() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [step, setStep] = useState('password'); // password | mfa
  const [cred, setCred] = useState({ email: '', password: '' });
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [debugCode, setDebugCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(true);
  const [err, setErr] = useState('');
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const flashError = (message) => {
    setErr(message);
    setShake(true);
    window.setTimeout(() => setShake(false), 450);
  };

  const onField = (e) => {
    setCred((c) => ({ ...c, [e.target.name]: e.target.value }));
    if (err) setErr('');
  };

  const finishLogin = (data) => {
    const token = data?.token || data?.accessToken || data?.admToken;
    if (!token) throw new Error('Login succeeded but token missing from response');
    localStorage.setItem('adm-token', token);
    if (data?.user) {
      localStorage.setItem('adm-user', JSON.stringify(data.user));
    }
    nav('/admin/users', { replace: true });
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const res = await httpAdmin.publicFetch('/api/admin/login', {
        method: 'POST',
        body: JSON.stringify({
          email: cred.email.trim().toLowerCase(),
          password: cred.password,
        }),
      });
      if (!res.ok) await readError(res);
      const data = await res.json();
      if (data.mfaRequired && data.mfaToken) {
        setMfaToken(data.mfaToken);
        setEmailHint(data.emailHint || '');
        setDebugCode(data.debugCode || '');
        setMfaCode(data.debugCode || '');
        setStep('mfa');
        return;
      }
      finishLogin(data);
    } catch (er) {
      flashError(er.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const res = await httpAdmin.publicFetch('/api/admin/login/mfa', {
        method: 'POST',
        body: JSON.stringify({
          mfaToken,
          code: mfaCode.trim(),
          trustDevice,
        }),
      });
      if (!res.ok) await readError(res);
      const data = await res.json();
      finishLogin(data);
    } catch (er) {
      flashError(er.message || 'Verification failed');
      if (er.code === 'MFA_EXPIRED') {
        setStep('password');
        setMfaToken('');
        setMfaCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page auth-page--split auth-page--login auth-page--admin">
      <AdminBrandAside label="Ehgezly Admin" />

      <main className="auth-main">
        <div className={`auth-form-panel${shake ? ' admin-login-panel--shake' : ''}`}>
          <Link to="/" className="admin-login-home-logo" aria-label="Ehgezly home">
            <ELogo size="small" />
          </Link>

          <header className="auth-form-head">
            <h2>Ehgezly Admin</h2>
            <p className="admin-login-subtitle">
              {step === 'mfa'
                ? 'Enter the verification code sent to your email'
                : 'Sign in to manage the platform'}
            </p>
          </header>

          <SessionBanner />

          {err && (
            <div className="error-message admin-login-error" role="alert">
              {err}
            </div>
          )}

          {step === 'password' ? (
            <form className="auth-form" onSubmit={submitPassword} aria-busy={loading} noValidate>
              <div className="form-group">
                <label className="form-label" htmlFor="admin-login-email">
                  Email Address
                </label>
                <input
                  id="admin-login-email"
                  type="email"
                  name="email"
                  placeholder={t('enterAdminEmail')}
                  value={cred.email}
                  onChange={onField}
                  autoComplete="email"
                  className={`form-input${err ? ' admin-login-input--invalid' : ''}`}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="admin-login-password">
                  Password
                </label>
                <div className="auth-password-field">
                  <input
                    id="admin-login-password"
                    type={showPwd ? 'text' : 'password'}
                    name="password"
                    placeholder={t('enterPassword')}
                    value={cred.password}
                    onChange={onField}
                    autoComplete="current-password"
                    className={`form-input${err ? ' admin-login-input--invalid' : ''}`}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPwd((s) => !s)}
                    aria-pressed={showPwd}
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                  >
                    {showPwd ? <FaEyeSlash aria-hidden="true" /> : <FaEye aria-hidden="true" />}
                  </button>
                </div>
              </div>

              <div className="form-options admin-login-form-options">
                <span className="admin-login-form-options__spacer" aria-hidden="true" />
                <Link to="/admin/forgot-password" className="forgot-password-link admin-login-forgot-link">
                  Forgot password?
                </Link>
              </div>

              <button type="submit" className="auth-button" disabled={loading}>
                <span className="button-content">
                  {loading ? <span className="loading-spinner" /> : 'Continue'}
                </span>
              </button>
            </form>
          ) : (
            <form className="auth-form" onSubmit={submitMfa} aria-busy={loading} noValidate>
              {emailHint && (
                <p className="admin-login-mfa-hint" role="status">
                  <span className="admin-login-mfa-hint__label">Code sent to</span>
                  <span className="admin-login-mfa-hint__email">{emailHint}</span>
                </p>
              )}
              {debugCode && (
                <p className="admin-login-dev-code" role="note">
                  Dev code: {debugCode}
                </p>
              )}
              <div className="form-group">
                <label className="form-label" htmlFor="admin-login-mfa">
                  6-digit code
                </label>
                <input
                  id="admin-login-mfa"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(e) => {
                    setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                    if (err) setErr('');
                  }}
                  className={`form-input${err ? ' admin-login-input--invalid' : ''}`}
                  required
                />
              </div>
              <label className="admin-login-trust">
                <input
                  type="checkbox"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                />
                <span>
                  Trust this laptop for 30 days
                  <span className="admin-login-trust__hint">
                    Skip the email code next time (password still required)
                  </span>
                </span>
              </label>
              <button type="submit" className="auth-button" disabled={loading || mfaCode.length < 6}>
                <span className="button-content">
                  {loading ? <span className="loading-spinner" /> : 'Verify & Sign In'}
                </span>
              </button>
              <button
                type="button"
                className="admin-login-back-btn"
                onClick={() => {
                  setStep('password');
                  setMfaToken('');
                  setMfaCode('');
                  setErr('');
                }}
              >
                Back to email & password
              </button>
            </form>
          )}

        </div>
      </main>
    </div>
  );
}

/** Forgot / reset password */
export function AdminForgotPasswordPage() {
  const nav = useNavigate();
  const [step, setStep] = useState('email'); // email | code
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState('');
  const [message, setMessage] = useState('');
  const [debugCode, setDebugCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);

  const flashError = (msg) => {
    setErr(msg);
    setShake(true);
    window.setTimeout(() => setShake(false), 450);
  };

  const sendCode = async (e) => {
    e.preventDefault();
    setErr('');
    setMessage('');
    if (!email.trim()) return flashError('Email is required');
    setLoading(true);
    try {
      const res = await httpAdmin.publicFetch('/api/admin/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (!res.ok) await readError(res);
      const data = await res.json();
      setMessage(data.message || 'If that email belongs to an admin account, a reset code was sent.');
      setDebugCode(data.debugCode || '');
      if (data.debugCode) setCode(data.debugCode);
      if (data.debugHint && !data.debugCode) {
        flashError(data.debugHint);
      }
      setStep('code');
    } catch (er) {
      flashError(er.message);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setErr('');
    setMessage('');
    if (!code.trim() || code.trim().length !== 4) {
      return flashError('Enter the 4-digit code from your email');
    }
    if (!password || password.length < 8) {
      return flashError('Password must be at least 8 characters');
    }
    if (password !== confirm) return flashError('Passwords do not match');
    setLoading(true);
    try {
      const res = await httpAdmin.publicFetch('/api/admin/reset-password', {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          verificationCode: code.trim(),
          newPassword: password,
        }),
      });
      if (!res.ok) await readError(res);
      setMessage('Password updated! Redirecting to sign in…');
      window.setTimeout(() => nav('/admin/login', { replace: true }), 1600);
    } catch (er) {
      flashError(er.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page auth-page--split auth-page--forgot auth-page--admin">
      <AdminBrandAside label="Reset admin password" />

      <main className="auth-main">
        <div className={`auth-form-panel${shake ? ' admin-login-panel--shake' : ''}`}>
          <Link to="/admin/login" className="admin-login-home-logo" aria-label="Back to admin login">
            <ELogo size="small" />
          </Link>

          <header className="auth-form-head">
            <h2>Reset admin password</h2>
            <p>
              {step === 'email'
                ? 'We will email a 4-digit code to your admin address'
                : 'Enter the code and choose a new password'}
            </p>
          </header>

          {err && (
            <div className="error-message admin-login-error" role="alert">
              {err}
            </div>
          )}
          {message && (
            <div className="admin-login-banner admin-login-banner--success" role="status">
              {message}
            </div>
          )}

          {step === 'email' ? (
            <form className="auth-form" onSubmit={sendCode} aria-busy={loading} noValidate>
              <div className="form-group">
                <label className="form-label" htmlFor="admin-forgot-email">
                  Admin email
                </label>
                <input
                  id="admin-forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (err) setErr('');
                  }}
                  className={`form-input${err ? ' admin-login-input--invalid' : ''}`}
                  autoComplete="email"
                  required
                />
              </div>
              <button type="submit" className="auth-button" disabled={loading}>
                <span className="button-content">
                  {loading ? <span className="loading-spinner" /> : 'Send reset code'}
                </span>
              </button>
              <Link to="/admin/login" className="admin-login-back-btn">
                Back to sign in
              </Link>
            </form>
          ) : (
            <form className="auth-form" onSubmit={resetPassword} aria-busy={loading} noValidate>
              {debugCode && (
                <p className="admin-login-dev-code" role="note">
                  Dev code: {debugCode}
                </p>
              )}
              <div className="form-group">
                <label className="form-label" htmlFor="admin-forgot-code">
                  4-digit code
                </label>
                <input
                  id="admin-forgot-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  className={`form-input${err ? ' admin-login-input--invalid' : ''}`}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="admin-forgot-password">
                  New password
                </label>
                <div className="auth-password-field">
                  <input
                    id="admin-forgot-password"
                    type={showPwd ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`form-input${err ? ' admin-login-input--invalid' : ''}`}
                    autoComplete="new-password"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPwd((s) => !s)}
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                  >
                    {showPwd ? <FaEyeSlash aria-hidden="true" /> : <FaEye aria-hidden="true" />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="admin-forgot-confirm">
                  Confirm password
                </label>
                <input
                  id="admin-forgot-confirm"
                  type={showPwd ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className={`form-input${err ? ' admin-login-input--invalid' : ''}`}
                  autoComplete="new-password"
                  required
                  minLength={8}
                />
              </div>
              <button type="submit" className="auth-button" disabled={loading}>
                <span className="button-content">
                  {loading ? <span className="loading-spinner" /> : 'Update password'}
                </span>
              </button>
              <button
                type="button"
                className="admin-login-back-btn"
                onClick={() => {
                  setStep('email');
                  setErr('');
                }}
              >
                Use a different email
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}

export default AdminLoginPage;
